import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, MessageCircle, Send, type LucideIcon } from 'lucide-react';
import AdminPagination from '../components/AdminPagination';
import { useNotification } from '../components/NotificationProvider';
import {
  AdminContactMessage,
  ContactMessageStatus,
  EmailDeliveryStatus,
  deleteContactMessage,
  fetchContactMessages,
  updateContactMessageStatus,
} from '../services/admin.api';
import { useTranslations } from '../translations';
import './AdminContactMessages.css';

const PAGE_SIZE = 10;

interface ContactDropdownOption<T extends string> {
  value: T;
  label: string;
}

interface ContactDropdownProps<T extends string> {
  ariaLabel: string;
  value: T;
  options: ContactDropdownOption<T>[];
  onChange: (value: T) => void;
  icon?: LucideIcon;
  disabled?: boolean;
}

const ContactDropdown = <T extends string,>({
  ariaLabel,
  value,
  options,
  onChange,
  icon: Icon,
  disabled = false,
}: ContactDropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((option) => option.value === value) ?? options[0];

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  return (
    <div className="admin-contact-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className="admin-contact-dropdown-trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={() => setIsOpen((current) => !current)}
      >
        {Icon && <Icon className="admin-contact-filter-icon" aria-hidden />}
        <span>{selectedOption.label}</span>
        <ChevronDown
          className={`admin-contact-dropdown-chevron ${isOpen ? 'open' : ''}`}
          aria-hidden
        />
      </button>

      {isOpen && (
        <div className="admin-contact-dropdown-menu" role="listbox" aria-label={ariaLabel}>
          {options.map((option) => (
            <button
              key={option.value || 'all'}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`admin-contact-dropdown-option ${option.value === value ? 'active' : ''}`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminContactMessages: React.FC = () => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [messages, setMessages] = useState<AdminContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [status, setStatus] = useState<ContactMessageStatus | ''>('');
  const [deliveryStatus, setDeliveryStatus] = useState<EmailDeliveryStatus | ''>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadMessages = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetchContactMessages({
        page: currentPage,
        size: PAGE_SIZE,
        status: status || undefined,
        deliveryStatus: deliveryStatus || undefined,
      });
      setMessages(response.content ?? []);
      setTotalPages(response.totalPages ?? 0);
    } catch (requestError) {
      console.error('Error loading contact messages:', requestError);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadMessages();
  }, [currentPage, status, deliveryStatus]);

  const replaceMessage = (updated: AdminContactMessage) => {
    setMessages((current) => current.map((message) => message.id === updated.id ? updated : message));
  };

  const handleToggle = async (message: AdminContactMessage) => {
    if (expandedId === message.id) {
      setExpandedId(null);
      return;
    }

    setExpandedId(message.id);
    if (message.status === 'NEW') {
      try {
        replaceMessage(await updateContactMessageStatus(message.id, 'READ'));
      } catch (requestError) {
        console.error('Error marking contact message as read:', requestError);
        showNotification(t.administration.contactMessages.updateError, 'error');
      }
    }
  };

  const handleStatusChange = async (messageId: string, nextStatus: ContactMessageStatus) => {
    setBusyId(messageId);
    try {
      replaceMessage(await updateContactMessageStatus(messageId, nextStatus));
      showNotification(t.administration.contactMessages.updateSuccess, 'success');
    } catch (requestError) {
      console.error('Error updating contact message:', requestError);
      showNotification(t.administration.contactMessages.updateError, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (message: AdminContactMessage) => {
    if (!window.confirm(t.administration.contactMessages.deleteConfirmation)) return;
    setBusyId(message.id);
    try {
      await deleteContactMessage(message.id);
      showNotification(t.administration.contactMessages.deleteSuccess, 'success');
      if (messages.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
      } else {
        await loadMessages();
      }
    } catch (requestError) {
      console.error('Error deleting contact message:', requestError);
      showNotification(t.administration.contactMessages.deleteError, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const formatDate = (value?: string | null) => value
    ? new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
    : '-';

  const messageStatusFilterOptions: ContactDropdownOption<ContactMessageStatus | ''>[] = [
    { value: '', label: t.administration.contactMessages.allStatuses },
    { value: 'NEW', label: t.administration.contactMessages.statusNew },
    { value: 'READ', label: t.administration.contactMessages.statusRead },
    { value: 'RESOLVED', label: t.administration.contactMessages.statusResolved },
  ];
  const deliveryStatusOptions: ContactDropdownOption<EmailDeliveryStatus | ''>[] = [
    { value: '', label: t.administration.contactMessages.allStatuses },
    { value: 'PENDING', label: t.administration.contactMessages.deliveryPending },
    { value: 'SENT', label: t.administration.contactMessages.deliverySent },
    { value: 'FAILED', label: t.administration.contactMessages.deliveryFailed },
  ];
  const messageStatusOptions: ContactDropdownOption<ContactMessageStatus>[] = messageStatusFilterOptions
    .filter((option): option is ContactDropdownOption<ContactMessageStatus> => option.value !== '');

  return (
    <div className="admin-contact-messages">
      <div className="admin-contact-filters">
        <label>
          <span>{t.administration.contactMessages.messageStatus}</span>
          <ContactDropdown
            ariaLabel={t.administration.contactMessages.messageStatus}
            value={status}
            options={messageStatusFilterOptions}
            icon={MessageCircle}
            onChange={(nextStatus) => {
              setStatus(nextStatus);
              setCurrentPage(1);
            }}
          />
        </label>
        <label>
          <span>{t.administration.contactMessages.deliveryStatus}</span>
          <ContactDropdown
            ariaLabel={t.administration.contactMessages.deliveryStatus}
            value={deliveryStatus}
            options={deliveryStatusOptions}
            icon={Send}
            onChange={(nextStatus) => {
              setDeliveryStatus(nextStatus);
              setCurrentPage(1);
            }}
          />
        </label>
      </div>

      {loading ? (
        <div className="admin-contact-state">{t.common.loading}</div>
      ) : error ? (
        <div className="admin-contact-state admin-contact-error">{t.administration.contactMessages.loadError}</div>
      ) : messages.length === 0 ? (
        <div className="admin-contact-state">{t.administration.contactMessages.empty}</div>
      ) : (
        <div className="admin-contact-list">
          {messages.map((message) => {
            const expanded = expandedId === message.id;
            return (
              <article className={`admin-contact-item ${message.status === 'NEW' ? 'admin-contact-item-new' : ''}`} key={message.id}>
                <button type="button" className="admin-contact-summary" onClick={() => void handleToggle(message)} aria-expanded={expanded}>
                  <span className="admin-contact-sender">
                    <strong>{message.name}</strong>
                    <span>{message.email}</span>
                  </span>
                  <span className="admin-contact-subject">{message.subject}</span>
                  <span className="admin-contact-meta">
                    <span className={`admin-contact-badge status-${message.status.toLowerCase()}`}>{t.administration.contactMessages[`status${message.status === 'NEW' ? 'New' : message.status === 'READ' ? 'Read' : 'Resolved'}`]}</span>
                    <time>{formatDate(message.createdAt)}</time>
                  </span>
                </button>

                {expanded && (
                  <div className="admin-contact-detail">
                    <p>{message.message}</p>
                    <dl>
                      <div><dt>{t.administration.contactMessages.deliveryStatus}</dt><dd>{t.administration.contactMessages[`delivery${message.deliveryStatus === 'PENDING' ? 'Pending' : message.deliveryStatus === 'SENT' ? 'Sent' : 'Failed'}`]}</dd></div>
                      <div><dt>{t.administration.contactMessages.sentAt}</dt><dd>{formatDate(message.sentAt)}</dd></div>
                    </dl>
                    <div className="admin-contact-actions">
                      <ContactDropdown
                        ariaLabel={t.administration.contactMessages.messageStatus}
                        value={message.status}
                        disabled={busyId === message.id}
                        options={messageStatusOptions}
                        icon={MessageCircle}
                        onChange={(nextStatus) => void handleStatusChange(message.id, nextStatus)}
                      />
                      <button type="button" className="admin-contact-delete" disabled={busyId === message.id} onClick={() => void handleDelete(message)}>
                        {t.administration.contactMessages.delete}
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      <AdminPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
  );
};

export default AdminContactMessages;
